import C from "@/components/sidebar/StandardSidebar.module.css";

import { localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { PriceBlock } from "@/components/sidebar/PriceBlock.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { ResourceCreateLinks } from "@/modules/api/resourceCreateLinks.ts";
import { CommitmentPriceBlock } from "@/modules/metal/commitment/CommitmentPriceBlock.tsx";
import { allMetalPricedQueryAtom, type MetalListItemWithPrice } from "@/modules/metal/joinPricesQuery.ts";
import { leaseLink } from "@/modules/metal/links.ts";
import { OsIcon } from "@/modules/metal/os/OsIcon.tsx";
import { MetalStatusBadge } from "@/modules/metal/status/MetalStatus.tsx";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { datetimeToDate } from "@warrenio/api-spec/conversion";
import { useAtomValue } from "jotai";

const sortFields = [
    {
        id: "name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => i.spec.title),
    },
    { id: "created_at", title: <Trans>Created date</Trans>, getValue: (i) => datetimeToDate(i.created_at).getTime() },
    { id: "location", title: <Trans>Location</Trans>, getValue: (i) => i.location },
] satisfies SimpleSortField<MetalListItemWithPrice>[];

export default function MetalList() {
    const { t } = useLingui();
    return (
        <SidebarList
            data={(props) => <MetalData {...props} />}
            title={t`Bare metal`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                action: ResourceCreateLinks.bare_metal,
            }}
            listId="metal"
        />
    );
}

function MetalData({ queryString, sortId, sortAsc }: SidebarListState) {
    const query = useAtomValue(allMetalPricedQueryAtom);

    let metalItems = getFilteredItems(queryString, [...query.data.values()], (item) => [item.spec.title]);
    metalItems = getSortedItems(sortFields, { sortId, sortAsc }, metalItems);

    return (
        <ListResult
            items={metalItems.map((obj) => (
                <MetalBlock key={obj.uuid} item={obj} />
            ))}
            isSearching={!!queryString}
            query={query}
            emptyItem={
                <EmptyItem icon="jp-host-icon">
                    <Trans>Create a new resource and get your services running in minutes.</Trans>
                </EmptyItem>
            }
        />
    );
}

export function MetalBlock({ item }: { item: MetalListItemWithPrice }) {
    const { ip_public_v4, display_name, os_id, location, status, billing_account_id, pricing } = item;

    return (
        <StandardBlock link={leaseLink(item)}>
            <div className={C.StandardBlockIcon}>
                <OsIcon os_id={os_id} className="size-1rem" />
            </div>

            <div className={cn(C.StandardBlockName, "text-ellipsis")}>
                <b className="text-ellipsis">{display_name}</b>
                <div className="text-ellipsis color-primary">
                    {ip_public_v4} / {location}
                </div>
            </div>

            {status === "in_use" ? (
                pricing.pricingType === "payg" ? (
                    <PriceBlock price={pricing.price} billingAccountId={billing_account_id} />
                ) : (
                    <CommitmentPriceBlock commitment={pricing.commitment} />
                )
            ) : (
                <div className={C.StandardBlockType}>
                    <MetalStatusBadge value={status} />
                </div>
            )}
        </StandardBlock>
    );
}
